import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useInvitationService } from "../services/invitation.service";

// Types
export interface Invitation {
  id: string;
  email: string;
  invitation_token: string;
  is_accepted: boolean;
  invited_by_id: string | null;
  assigned_plan_code: string | null;
  expires_at: string;
  created_at: string;
  updated_at: string | null;
  status: string; // New field: "pending", "accepted", or "expired"
  tenant_id: string | null;
  tenant_name: string | null;
}

export interface InvitationsResponse {
  invitations: Invitation[];
  total: number;
}

// Query keys
export const invitationKeys = {
  all: ['invitations'] as const,
  lists: () => [...invitationKeys.all, 'list'] as const,
  details: () => [...invitationKeys.all, 'detail'] as const,
  detail: (id: string) => [...invitationKeys.details(), id] as const,
};

// Default configuration
const DEFAULT_STALE_TIME = 1 * 60 * 1000; // 1 minute
const DEFAULT_CACHE_TIME = 5 * 60 * 1000; // 5 minutes

export const useInvitations = (options?: { enabled?: boolean }) => {
  const invitationService = useInvitationService();
  const queryClient = useQueryClient();

  // Get all invitations
  const query = useQuery<InvitationsResponse, Error>({
    queryKey: invitationKeys.lists(),
    queryFn: async () => {
      const response = await invitationService.getAllInvitations();
      if (!response) {
        throw new Error("No data returned from the server");
      }
      return response;
    },
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_CACHE_TIME,
    refetchInterval: 30000, // 30 seconds
    ...options
  });

  // Create invitation mutation. Payload matches backend: email, assigned_plan_code?, tenant_id?, tenant_name?
  const useCreateInvitation = () => {
    return useMutation({
      mutationFn: async (data: {
        email: string;
        assigned_plan_code?: string;
        tenant_id?: string;
        tenant_name?: string;
      }) => {
        return await invitationService.createInvitation(data);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: invitationKeys.lists() });
      }
    });
  };

  // Delete invitation mutation
  const useDeleteInvitation = () => {
    return useMutation({
      mutationFn: async (invitationId: string) => {
        return await invitationService.deleteInvitation(invitationId);
      },
      onSuccess: () => {
        // Invalidate the invitations list query to refetch
        queryClient.invalidateQueries({ queryKey: invitationKeys.lists() });
      }
    });
  };

  return {
    ...query,
    useCreateInvitation,
    useDeleteInvitation
  };
}; 